import Swal from 'sweetalert2'
import i18n from '@zing/shared/i18n/translate/config'
import { PopUpContainer, PopUpHeader, PopUpTitle, PopUpParagraph, PopUpContent } from './style'
import { withPopupTheme } from '../withPopupTheme'
import { mergedTheme } from '../../../assets/theme/mergedTheme'
import { renderToString } from 'react-dom/server'
import React from 'react'

export interface ConnectModalProps {
  startupId: string
  title: string
  message: string | React.JSX.Element
  onConfirm?: (data: any) => void
}

export interface RequestPayload {
  startupId: string
  companyId: string
  source: string
  responsible: string
  areaInterest: string
  description: string
}

export const connectModal = ({ startupId, title, message, onConfirm }: ConnectModalProps): void => {
  const content = withPopupTheme(
    <PopUpContainer>
      <PopUpHeader>
        <PopUpTitle>{title}</PopUpTitle>
      </PopUpHeader>
      <PopUpContent>
        <PopUpParagraph>{message}</PopUpParagraph>
      </PopUpContent>
    </PopUpContainer>
  )

  Swal.fire({
    showCloseButton: true,
    showConfirmButton: true,
    confirmButtonText: i18n.t('Save'),
    cancelButtonText: i18n.t('Cancel'),
    showCancelButton: true,
    reverseButtons: true,
    allowOutsideClick: true,
    customClass: {
      popup: 'connect-modal',
      closeButton: 'connect-modal-close',
      container: 'connect-modal-container',
      actions: 'connect-actions',
      confirmButton: 'connect-confirm',
      cancelButton: 'connect-cancel'
    },
    html: renderToString(content),
    width: '800px',
    padding: 0,
    backdrop: 'rgba(0,0,0,0.4)',
    preConfirm: (): any => {
      const modalElement = document.querySelector('.connect-modal')
      const sourceSelect = modalElement?.querySelector('select') as HTMLSelectElement | null
      const inputs = modalElement?.querySelectorAll('input[type="text"]') as NodeListOf<HTMLInputElement> | null
      const descriptionTextarea = modalElement?.querySelector('textarea') as HTMLTextAreaElement | null
      const sourceError = modalElement?.querySelector('small[data-error-for="source"]') as HTMLElement | null
      const responsibleError = modalElement?.querySelector('small[data-error-for="responsible"]') as HTMLElement | null
      const areaInterestError = modalElement?.querySelector('small[data-error-for="areaInterest"]') as HTMLElement | null

      if (sourceError !== null) {
        sourceError.textContent = ''
      }
      if (responsibleError !== null) {
        responsibleError.textContent = ''
      }
      if (areaInterestError !== null) {
        areaInterestError.textContent = ''
      }

      const sourceVal = sourceSelect?.value ?? ''
      const responsibleVal = inputs?.[0]?.value ?? ''
      const areaInterestVal = inputs?.[1]?.value ?? ''

      let hasError = false
      if (sourceVal === '') {
        hasError = true
        if (sourceError !== null) {
          sourceError.textContent = i18n.t('Field cannot be empty')
        }
        if (sourceSelect !== null) {
          sourceSelect.style.borderColor = mergedTheme.colors.error
          if (sourceSelect.getAttribute('data-has-blur-handler') !== 'true') {
            sourceSelect.setAttribute('data-has-blur-handler', 'true')
            sourceSelect.addEventListener('blur', () => {
              const currentVal = sourceSelect.value
              if (currentVal !== '') {
                sourceSelect.style.borderColor = ''
                if (sourceError !== null) sourceError.textContent = ''
              }
            })
          }
        }
      }
      if (String(responsibleVal).trim() === '') {
        hasError = true
        if (responsibleError !== null) {
          responsibleError.textContent = i18n.t('Field cannot be empty')
        }
        const responsibleInput = inputs?.[0] ?? null
        if (responsibleInput !== null) {
          responsibleInput.style.borderColor = mergedTheme.colors.error
          if (responsibleInput.getAttribute('data-has-blur-handler') !== 'true') {
            responsibleInput.setAttribute('data-has-blur-handler', 'true')
            responsibleInput.addEventListener('blur', () => {
              const currentVal = String(responsibleInput.value).trim()
              if (currentVal !== '') {
                responsibleInput.style.borderColor = ''
                if (responsibleError !== null) responsibleError.textContent = ''
              }
            })
          }
        }
      }
      if (String(areaInterestVal).trim() === '') {
        hasError = true
        if (areaInterestError !== null) {
          areaInterestError.textContent = i18n.t('Field cannot be empty')
        }
        const areaInterestInput = inputs?.[1] ?? null
        if (areaInterestInput !== null) {
          areaInterestInput.style.borderColor = mergedTheme.colors.error
          if (areaInterestInput.getAttribute('data-has-blur-handler') !== 'true') {
            areaInterestInput.setAttribute('data-has-blur-handler', 'true')
            areaInterestInput.addEventListener('blur', () => {
              const currentVal = String(areaInterestInput.value).trim()
              if (currentVal !== '') {
                areaInterestInput.style.borderColor = ''
                if (areaInterestError !== null) areaInterestError.textContent = ''
              }
            })
          }
        }
      }

      if (hasError) return false

      return {
        source: sourceVal,
        responsible: responsibleVal,
        areaInterest: areaInterestVal,
        description: descriptionTextarea?.value ?? ''
      }
    }
  }).then((result) => {
    if (result?.isConfirmed && onConfirm !== undefined) {
      const modalData = result.value as {
        source: string
        responsible: string
        areaInterest: string
        description: string
      }
      onConfirm({
        startupId,
        ...modalData
      })
    }
  })
}